// Layer 4 (UI) — the PM schedule's work lanes (polish.P21, variant B's lanes on
// comp A's PM schedule tab): the windows lane, then one lane per open task,
// each block at the day shift of its due date and as wide as its hours; the
// cursor and the bearing's projected limit ruled through every lane. State
// colour marks only the exceptions — overdue, due today and the draft stop
// (dashed); scheduled work is neutral. Presentational: every block is a field
// of `pmLanes()`.

import { useLayoutEffect, useRef, useState } from 'react';
import type { LaneBlock, PmLanesRead } from './PmLanes.transform';
import { LANE_PAD_R, laneAxis, laneLabelPlacements } from './PmLanes.transform';

const ROW_H = 19;
const AXIS_H = 20;
const TEXT_PX = 11;

function blockLabelFill(kind: LaneBlock['kind']): string {
  if (kind === 'overdue') return 'var(--state-blocked)';
  if (kind === 'draft') return 'var(--ink-primary)';
  if (kind === 'window') return 'var(--ink-tertiary)';
  return 'var(--ink-secondary)';
}

export function PmLanes({ read }: { read: PmLanesRead }) {
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const [width, setWidth] = useState(1100);
  useLayoutEffect(() => {
    const el = wrapperRef.current;
    if (!el) return undefined;
    const measure = () => setWidth(Math.max(560, Math.round(el.getBoundingClientRect().width)));
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const x = laneAxis(read, width);
  const lanesH = read.lanes.length * ROW_H;
  // Label side per block, right to left along each lane (the pure placement in
  // `PmLanes.transform.ts`): a label sits right of its block, offset just past
  // the Now rule when its block ends inside the rule's clear air, and flips
  // left only when it would run into what the next block (or its label)
  // occupies; the draft always labels to its left.
  const placements = laneLabelPlacements({ lanes: read.lanes, width, axis: x, now: read.now });
  const height = lanesH + AXIS_H + 4;

  return (
    <div ref={wrapperRef} data-pm-lanes>
      <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`} className="block" role="img" aria-label="Open PM tasks and shutdown windows by day">
        {read.dayTicks.map((tick) => (
          <g key={tick.at}>
            <line x1={x(tick.at)} x2={x(tick.at)} y1={0} y2={lanesH} stroke="var(--color-divider)" />
            {x(tick.at) < width - LANE_PAD_R - 14 ? (
              <text x={x(tick.at)} y={height - 5} fontSize={10} textAnchor="middle" fill="var(--ink-tertiary)">
                {tick.label}
              </text>
            ) : null}
          </g>
        ))}
        <line x1={x(read.now)} x2={x(read.now)} y1={0} y2={lanesH} stroke="var(--color-accent-500)" strokeWidth={1.2} data-pm-now={read.now} />
        {read.limitAt ? (
          <line x1={x(read.limitAt)} x2={x(read.limitAt)} y1={0} y2={lanesH} stroke="var(--state-blocked)" strokeWidth={1.2} strokeDasharray="2 3" data-pm-limit={read.limitAt} />
        ) : null}
        {read.lanes.map((lane, index) => {
          const top = index * ROW_H;
          const by = top + 4;
          const bh = ROW_H - 8;
          return (
            <g key={lane.id} data-pm-lane={lane.id}>
              {index > 0 ? <line x1={0} x2={width - LANE_PAD_R} y1={top} y2={top} stroke="var(--color-divider)" /> : null}
              <text
                x={0}
                y={top + 13}
                fontSize={lane.group ? 10.5 : 11.5}
                fill={lane.group ? 'var(--ink-tertiary)' : 'var(--ink-primary)'}
                fontWeight={lane.group ? 600 : 400}
                letterSpacing={lane.group ? '0.07em' : undefined}
              >
                {lane.group ? lane.label.toUpperCase() : lane.label}
              </text>
              {lane.blocks.map((block, blockIndex) => {
                const bx = x(block.startAt);
                const bw = Math.max(3, x(block.endAt) - bx);
                const fill = blockLabelFill(block.kind);
                const placement = placements[index]?.[blockIndex] ?? { side: 'right' as const, x: bx + bw + 6 };
                const lx = placement.x;
                const anchor = placement.side === 'right' ? 'start' : 'end';
                return (
                  <g key={`${block.kind}-${block.startAt}`} data-pm-block={block.kind}>
                    {block.kind === 'overdue' ? (
                      <rect x={bx} y={by} width={bw} height={bh} rx={2} fill="color-mix(in srgb, var(--state-blocked) 9%, transparent)" stroke="var(--state-blocked)" strokeWidth={1} />
                    ) : null}
                    {block.kind === 'due-today' ? <rect x={bx} y={by} width={bw} height={bh} rx={2} fill="var(--state-warning)" /> : null}
                    {block.kind === 'draft' ? (
                      <rect x={bx} y={by} width={bw} height={bh} rx={2} fill="none" stroke="var(--ink-primary)" strokeDasharray="3 2" />
                    ) : null}
                    {block.kind === 'window' ? <rect x={bx} y={by} width={bw} height={bh} rx={2} fill="var(--ink-tertiary)" opacity={0.45} /> : null}
                    {block.kind === 'scheduled' ? <rect x={bx} y={by} width={bw} height={bh} rx={2} fill="var(--ink-secondary)" /> : null}
                    <text
                      x={lx}
                      y={by + 9}
                      fontSize={TEXT_PX}
                      textAnchor={anchor}
                      fill={fill}
                      fontWeight={block.kind === 'overdue' || block.kind === 'draft' ? 600 : 400}
                      stroke="var(--surface-2)"
                      strokeWidth={3}
                      strokeLinejoin="round"
                      paintOrder="stroke"
                    >
                      {block.label}
                    </text>
                  </g>
                );
              })}
            </g>
          );
        })}
      </svg>
    </div>
  );
}
